import { useQuery } from '@tanstack/react-query';
import {
  Activity, Bell, Building2, Camera, ClipboardList, Cpu, Film, HardDrive, LayoutDashboard, LogOut, MapPin, Menu, Moon,
  MonitorPlay, Settings, Sun, Users,
} from 'lucide-react';
import { Component, Suspense, useEffect, useState, type ReactNode } from 'react';
import { Empty, ErrorState, Loading } from './ui';
import { NavLink, Outlet, useLocation, useOutletContext } from 'react-router';
import { api } from './api/client';
import type { Dashboard } from './api/types';
import { ROLE_LABEL } from './format';
import { useSession } from './session';

function useTheme() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    try {
      return (localStorage.getItem('cftv.theme') as 'dark' | 'light') ?? 'dark';
    } catch {
      return 'dark';
    }
  });
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem('cftv.theme', theme);
    } catch {}
  }, [theme]);
  return [theme, setTheme] as const;
}

export function Layout() {
  const s = useSession();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useTheme();
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  const pending = useQuery({
    queryKey: ['c', s.companyId, 'dashboard'],
    enabled: !!s.companyId && s.can('camera.read'),
    queryFn: () => api.get<Dashboard>(`/api/v1/companies/${s.companyId}/dashboard`),
    refetchInterval: 30_000,
  });
  const role = s.company?.access?.role;

  const item = (to: string, icon: ReactNode, label: string, extra?: ReactNode) => (
    <NavLink to={to} end={to === '/'}>{icon}<span>{label}</span>{extra}</NavLink>
  );

  return (
    <div className={`shell${open ? ' nav-open' : ''}`}>
      {open && <div className="scrim" onClick={() => setOpen(false)} />}
      <aside className="sidebar" aria-label="Navegação principal">
        <div className="brand">
          <span className="brand-mark"><Activity size={16} /></span>
          CFTV Central
        </div>
        {s.companies.length > 1 ? (
          <div className="company-switch">
            <select className="input" aria-label="Empresa" value={s.companyId ?? ''} onChange={(e) => s.setCompanyId(e.target.value)}>
              {s.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        ) : (
          <div className="company-switch faint" style={{ padding: '0 4px' }}>{s.company?.name ?? s.companies[0]?.name ?? 'Sem empresa'}</div>
        )}
        {s.companyId && (
          <nav className="nav">
            <div className="nav-section">Operação</div>
            {s.can('camera.read') && item('/', <LayoutDashboard size={16} />, 'Painel')}
            {s.can('live.view') && item('/ao-vivo', <MonitorPlay size={16} />, 'Ao vivo')}
            {s.can('recording.view') && item('/gravacoes', <Film size={16} />, 'Gravações')}
            {s.can('event.read') && item('/eventos', <Bell size={16} />, 'Eventos', pending.data?.events.pending ? <span className="count">{pending.data.events.pending}</span> : null)}
            <div className="nav-section">Cadastro</div>
            {s.can('camera.read') && item('/cameras', <Camera size={16} />, 'Câmeras')}
            {s.can('site.read') && item('/locais', <MapPin size={16} />, 'Locais')}
            {s.can('gateway.read') && item('/gateways', <Cpu size={16} />, 'Gateways')}
            {(s.can('member.read') || s.can('storage.read') || s.can('audit.read')) && <div className="nav-section">Administração</div>}
            {s.can('member.read') && item('/usuarios', <Users size={16} />, 'Usuários')}
            {s.can('storage.read') && item('/armazenamento', <HardDrive size={16} />, 'Armazenamento')}
            {s.can('audit.read') && item('/auditoria', <ClipboardList size={16} />, 'Auditoria')}
            {item('/configuracoes', <Settings size={16} />, 'Configurações')}
          </nav>
        )}
        {s.me.user.isPlatformAdmin && (
          <nav className="nav">
            <div className="nav-section">Plataforma</div>
            {item('/plataforma', <Building2 size={16} />, 'Empresas e sistema')}
          </nav>
        )}
        <div className="sidebar-foot">
          <div>
            <div style={{ fontWeight: 600 }}>{s.me.user.name}</div>
            <div className="faint" style={{ fontSize: 12 }}>{s.me.user.email}{role ? ` · ${ROLE_LABEL[role] ?? role}` : ''}</div>
          </div>
          <div className="row">
            <button className="btn sm" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} aria-label="Alternar tema">
              {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />} Tema
            </button>
            <button className="btn sm" onClick={() => void s.logout()}><LogOut size={14} /> Sair</button>
          </div>
        </div>
      </aside>
      <div className="main">
        {s.companyId ? (
          <ErrorBoundary key={loc.pathname}>
            <Suspense fallback={<Loading />}><Outlet context={{ openNav: () => setOpen(true) }} /></Suspense>
          </ErrorBoundary>
        ) : loc.pathname === '/plataforma' && s.me.user.isPlatformAdmin ? (
          <Suspense fallback={<Loading />}><Outlet context={{ openNav: () => setOpen(true) }} /></Suspense>
        ) : (
          <>
            <PageHeaderBare title="Sem empresa" onMenu={() => setOpen(true)} />
            <div className="content">
              <Empty icon={<Building2 size={28} />} title="Nenhuma empresa disponível">
                {s.me.user.isPlatformAdmin ? <>Crie a primeira empresa em <NavLink to="/plataforma">Empresas e sistema</NavLink>.</> : 'Seu usuário ainda não está vinculado a nenhuma empresa ativa. Fale com o administrador.'}
              </Empty>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PageHeaderBare({ title, onMenu }: { title: string; onMenu: () => void }) {
  return (
    <header className="topbar">
      <button className="btn ghost icon menu-btn" aria-label="Abrir menu" onClick={onMenu}><Menu size={18} /></button>
      <h1>{title}</h1>
    </header>
  );
}

/** Um erro de renderização em uma página não derruba o painel inteiro. */
class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return <div className="content"><ErrorState error={null} onRetry={() => this.setState({ error: null })} /></div>;
    }
    return this.props.children;
  }
}

/** Cabeçalho de página com botão de menu no celular. */
export function PageHeader({ title, children }: { title: ReactNode; children?: ReactNode }) {
  const ctx = useOutletContext<{ openNav: () => void } | undefined>();
  return (
    <header className="topbar">
      <button className="btn ghost icon menu-btn" aria-label="Abrir menu" onClick={() => ctx?.openNav()}>
        <Menu size={18} />
      </button>
      <h1>{title}</h1>
      <span className="spacer" />
      {children}
    </header>
  );
}
